import { useState } from "react";
import { useIntl } from "react-intl";
import { Button, Checkbox, Icon, Input, Modal, Table } from "semantic-ui-react";
import { toast } from "../..";
import { useDispatch } from "react-redux";
import { IPaymentType } from "../../models/paymentType";
import {
    deletePaymentTypeByGuid,
    editPaymentTypeAsync,
    orderPaymentType,
} from "../../api/paymentTypes";
import { fetchPaymentTypes } from "../../store/paymentTypesReducer";

interface Props {
    paymentType: IPaymentType;
    paymentTypeCount: number;
    rowIndex: number;
}

export const PaymentTypeRow = ({ paymentType, paymentTypeCount, rowIndex }: Props) => {
    const intl = useIntl();
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [newPaymentType, setNewPaymentType] = useState(paymentType);
    const dispatch = useDispatch();

    const handleDeleteUser = async (guid: string) => {
        try {
            await deletePaymentTypeByGuid(guid);
            toast(intl.formatMessage({ id: "paymentTypeDeleted" }), true);
        } catch {
            toast(intl.formatMessage({ id: "unableToDeletePaymentType" }), false);
        } finally {
            fetchPaymentTypes(dispatch);
            setEditModalOpen(false);
        }
    };

    const handleEditExamination = async () => {
        try {
            await editPaymentTypeAsync(newPaymentType);
            toast(intl.formatMessage({ id: "paymentTypeEdited" }), true);
        } catch {
            toast(intl.formatMessage({ id: "unableToEditPaymentType" }), false);
        } finally {
            fetchPaymentTypes(dispatch);
            setEditModalOpen(false);
        }
    };

    const handleOrderNumber = async (order: boolean, guid: string) => {
        await orderPaymentType(guid, order);
        fetchPaymentTypes(dispatch);
    };

    return (
        <>
            <Table.Row>
                <Table.Cell>
                    <Icon
                        name="angle up"
                        link
                        onClick={() => handleOrderNumber(false, newPaymentType.guid)}
                        disabled={rowIndex === 0 ? true : false}
                    />
                    <Icon
                        name="angle down"
                        link
                        onClick={() => handleOrderNumber(true, newPaymentType.guid)}
                        disabled={rowIndex === paymentTypeCount - 1 ? true : false}
                    />
                </Table.Cell>
                <Table.Cell>{newPaymentType.name}</Table.Cell>
                <Table.Cell singleLine>
                    <Checkbox checked={!newPaymentType.disabled} />
                </Table.Cell>
                <Table.Cell width={1} singleLine>
                    <span className="buttons-container">
                        <Button
                            content={intl.formatMessage({ id: "edit" })}
                            basic
                            color="blue"
                            size="small"
                            onClick={() => setEditModalOpen(true)}
                        />
                        <Button
                            content={intl.formatMessage({ id: "delete" })}
                            negative
                            basic
                            size="small"
                            onClick={() => setDeleteModalOpen(true)}
                        />
                    </span>
                </Table.Cell>
            </Table.Row>
            <Modal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                dimmer="blurring"
                size="tiny"
                closeIcon
            >
                <Modal.Header>{intl.formatMessage({ id: "deletePaymentType" })}</Modal.Header>
                <Modal.Actions>
                    <Button
                        negative
                        onClick={() => setDeleteModalOpen(false)}
                        content={intl.formatMessage({ id: "cancel" })}
                    />
                    <Button
                        primary
                        onClick={() => handleDeleteUser(newPaymentType.guid)}
                        content={intl.formatMessage({ id: "confirm" })}
                    />
                </Modal.Actions>
            </Modal>
            <Modal
                open={editModalOpen}
                closeIcon
                onClose={() => setEditModalOpen(false)}
                dimmer="blurring"
                size="tiny"
            >
                <Modal.Content className="edit-modal">
                    <form className="add-admin-form">
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "paymentType" })}</label>
                            <Input
                                value={newPaymentType.name}
                                onChange={(e: any) =>
                                    setNewPaymentType({ ...newPaymentType, name: e.target.value })
                                }
                            />
                        </section>
                        <section className="input-container">
                            <Checkbox
                                label={intl.formatMessage({ id: "active" })}
                                checked={!newPaymentType.disabled }
                                onChange={(e, data) => {
                                    setNewPaymentType({
                                        ...newPaymentType,
                                        disabled: !data.checked!,
                                    });
                                }}
                            />
                        </section>
                    </form>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        content={intl.formatMessage({ id: "paymentTypeEdit" })}
                        disabled={!newPaymentType.name}
                        primary
                        onClick={handleEditExamination}
                        fluid
                    />
                </Modal.Actions>
            </Modal>
        </>
    );
};
